import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Check, X } from "lucide-react";

const API = "https://api.komorotel.com/api";

const euros = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} €`;
const jour  = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
const jourLong = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

const ETAT = {
  pending:          "En attente de confirmation",
  awaiting_deposit: "Règlement attendu",
  confirmed:        "Confirmée",
  completed:        "Terminée",
  cancelled:        "Annulée",
  no_show:          "Non présenté",
};

export default function OrgReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState(
    new URLSearchParams(window.location.search).get("pending") === "true" ? "pending" : "all"
  );

  const load = () => {
    setLoading(true);
    axios.get(`${API}/organization/reservations`, {
      params: filtre === "pending" ? { pending: true } : {},
      withCredentials: true,
    })
      .then(r => setReservations(r.data))
      .catch(() => toast.error("Impossible de charger les réservations."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtre]);

  const trancher = async (r, action) => {
    let reason = null;
    if (action === "reject") {
      reason = window.prompt("Motif du refus, transmis au voyageur (facultatif) :");
      if (reason === null) return;
    }
    try {
      await axios.patch(
        `${API}/organization/reservations/${r._id}/${action}`,
        action === "reject" ? { reason } : {},
        { withCredentials: true }
      );
      toast.success(action === "approve" ? "Réservation validée." : "Réservation refusée.");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Erreur.");
    }
  };

  const nom = (r) =>
    r.organization?.snapshot?.travellerName
    || `${r.guest?.firstName || ""} ${r.guest?.lastName || ""}`.trim()
    || "Voyageur";

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-8 sm:py-10 max-w-[980px]">

      <header className="mb-6">
        <h1 className="text-[23px] sm:text-[27px] leading-tight font-semibold tracking-[-0.01em] text-[var(--org-ink)]">
          Réservations
        </h1>
        <p className="text-[13.5px] sm:text-[13.5px] text-[var(--org-slate)] mt-1">
          Tous les séjours réglés par votre organisation
        </p>
      </header>

      <div className="flex gap-6 mb-1" style={{ borderBottom: "1px solid var(--org-line)" }}>
        {[
          { k: "all",     l: "Toutes" },
          { k: "pending", l: "À valider" },
        ].map(f => (
          <button
            key={f.k}
            onClick={() => setFiltre(f.k)}
            className={`pb-2.5 text-[15px] transition ${
              filtre === f.k ? "text-[var(--org-ink)]" : "text-[var(--org-slate)] hover:text-[var(--org-ink)]"
            }`}
            style={filtre === f.k ? { boxShadow: "inset 0 -2px 0 0 var(--org-forest)" } : undefined}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div>
        {loading ? (
          [...Array(4)].map((_, i) => (
            <div key={i} className="py-6" style={{ borderBottom: "1px solid var(--org-line)" }}>
              <div className="h-4 w-52 bg-gray-200/60 rounded animate-pulse" />
            </div>
          ))
        ) : reservations.length === 0 ? (
          <div className="py-10 max-w-xl">
            <p className="text-[14px] text-[var(--org-ink)]">
              {filtre === "pending" ? "Rien n'attend votre décision." : "Aucun séjour pour l'instant."}
            </p>
            <p className="text-[13.5px] text-[var(--org-slate)] mt-1.5 leading-relaxed">
              {filtre === "pending"
                ? "Une réservation n'arrive ici que si elle sort du cadre d'une mission. Tant que vos équipes restent dans l'enveloppe prévue, vous n'avez rien à faire."
                : "Réservez un hébergement sur komorotel.com en indiquant pour qui vous réservez, et le séjour apparaîtra ici."}
            </p>
          </div>
        ) : (
          reservations.map(r => {
            const org = r.organization || {};
            const attend = org.approvalStatus === "pending";

            return (
              <div
                key={r._id}
                className={`py-5 ${attend ? "pl-4 border-l-[3px]" : ""}`}
                style={{
                  borderBottom: "1px solid var(--org-line)",
                  ...(attend ? { borderLeftColor: "var(--org-sun)" } : {}),
                }}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <p className="text-[16px] sm:text-[14px] text-[var(--org-ink)] truncate">
                    {nom(r)}
                  </p>
                  <p className="org-num text-[15px] font-medium text-[var(--org-ink)] shrink-0">
                    {euros(r.totalPrice)}
                  </p>
                </div>

                <p className="text-[13px] text-[var(--org-slate)] mt-1">
                  {r.hotelId?.name || "Hébergement"}
                  {r.hotelId?.city ? `, ${r.hotelId.city}` : ""}
                </p>

                <p className="text-[13px] text-[var(--org-slate)] mt-0.5">
                  <span className="org-num">{jour(r.checkInDate)}</span> au{" "}
                  <span className="org-num">{jourLong(r.checkOutDate)}</span>, {r.totalNights} nuit
                  {r.totalNights > 1 ? "s" : ""}
                  {r.status !== "confirmed" && ETAT[r.status] ? `, ${ETAT[r.status].toLowerCase()}` : ""}
                </p>

                {org.snapshot?.missionTitle && (
                  <p className="text-[13px] text-[var(--org-slate)] mt-0.5">
                    Mission {org.snapshot.missionTitle}
                  </p>
                )}

                {attend && (
                  <div className="mt-3">
                    {org.outOfPolicy?.length > 0 && (
                      <ul className="text-[13.5px] mb-3" style={{ color: "#A5660B" }}>
                        {org.outOfPolicy.map((raison, i) => <li key={i}>{raison}</li>)}
                      </ul>
                    )}
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={() => trancher(r, "approve")}
                        className="flex items-center gap-1.5 px-3.5 py-[7px] rounded-lg text-[13.5px] font-semibold text-white transition hover:opacity-90"
                        style={{ background: "var(--org-forest)" }}
                      >
                        <Check size={15} strokeWidth={2} /> Valider
                      </button>
                      <button
                        onClick={() => trancher(r, "reject")}
                        className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[13.5px] text-[var(--org-slate)] hover:text-[var(--org-ink)] transition"
                        style={{ border: "1px solid var(--org-line)" }}
                      >
                        <X size={15} strokeWidth={2} /> Refuser
                      </button>
                      {org.approvalDueAt && (
                        <p className="text-[12.5px] text-[var(--org-slate)]">
                          Dates bloquées jusqu'au {jourLong(org.approvalDueAt)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {org.approvalStatus === "rejected" && org.rejectionReason && (
                  <p className="text-[13px] text-[var(--org-slate)] mt-2">
                    Refusée : {org.rejectionReason}
                  </p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
